import { GripVertical } from "lucide-react";
import type { Confirmation } from "../types/confirmations.types";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { cn } from "@/lib/utils";
import useTranslations from "@/shared/lang/useTranslations";

type ConfirmationCardPreviewProps = {
  confirmation: Confirmation;
  isOverlay?: boolean;
};

export const ConfirmationCardPreview = ({
  confirmation,
  isOverlay = true,
}: ConfirmationCardPreviewProps) => {
  const { t } = useTranslations("confirmations");
  const pendingConfig = {
    borderClass: "border-l-blue-300 dark:border-l-blue-400",
    badgeClass:
      "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
    label: t("card_status_pending"),
  } as const;
  return (
    <div
      role="presentation"
      data-confirmation-id={confirmation.id}
      className={cn(
        "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-5 rounded-xl border-l-4 pointer-events-none",
        pendingConfig.borderClass,
        isOverlay &&
          "shadow-xl ring-2 ring-green-500/30 scale-[1.02] cursor-grabbing"
      )}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="shrink-0 mt-0.5 text-slate-400 dark:text-slate-500"
            aria-hidden
          >
            <GripVertical className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {confirmation.currency}
            </p>
          </div>
        </div>
        <span
          className={cn(
            "shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-tight",
            pendingConfig.badgeClass
          )}
        >
          {pendingConfig.label}
        </span>
      </div>

      <div className="space-y-1.5 text-sm text-slate-500 dark:text-slate-400">
        <div className="flex justify-end text-right text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400 font-medium">
              {t("card_label_created_at")}{" "}
            </span>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {formatDateTime(confirmation.createdAt ?? confirmation.date)}
            </span>
          </div>
        </div>
        {confirmation.note && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[14px] block">
              <span className="text-slate-600 dark:text-slate-300 font-medium">{t("card_label_note")}</span>
              <span className="opacity-75"> {confirmation.note}</span>
            </span>
          </div>
        )}
        {confirmation.createdBy && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[14px]">
              <span className="text-slate-600 dark:text-slate-300 font-medium">{t("card_label_created_by")}</span>
              <span className="opacity-75"> {confirmation.createdBy}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
